import {
  doc,
  updateDoc,
  setDoc,
  arrayUnion,
  getDoc,
  Timestamp
} from 'firebase/firestore'
import * as Sentry from '@sentry/react'
import { db } from '@/firebase/config'

const dailyPresenceAttempts = new Set()

export function resetDailyPresenceTrackingForTests() {
  dailyPresenceAttempts.clear()
}

function captureLastLoginError(error, operation) {
  Sentry.captureException(error, {
    tags: {
      feature: 'analytics',
      area: 'last-login',
      operation
    }
  })
}

function addLastLoginBreadcrumb(message, data = {}) {
  Sentry.addBreadcrumb({
    category: 'last-login',
    level: 'info',
    message,
    data
  })
}

export function isSameLocalCalendarDay(value, now = new Date()) {
  if (!value) return false
  const date =
    typeof value.toDate === 'function' ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return false
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  )
}

function localDayKey(userId, now = new Date()) {
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${userId}:${year}-${month}-${day}`
}

export const trackDailyPresence = async (userId) => {
  if (!userId) return

  const key = localDayKey(userId)
  if (dailyPresenceAttempts.has(key)) return
  dailyPresenceAttempts.add(key)

  try {
    const userDocRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userDocRef)
    if (!userDoc.exists()) {
      dailyPresenceAttempts.delete(key)
      addLastLoginBreadcrumb('Daily presence skipped, user document missing')
      return
    }

    if (isSameLocalCalendarDay(userDoc.data()?.lastLogin)) {
      addLastLoginBreadcrumb('Daily presence skipped, lastLogin already today')
      return
    }

    await updateDoc(userDocRef, {
      lastLogin: Timestamp.now()
    })
    addLastLoginBreadcrumb('Daily presence updated lastLogin')
  } catch (error) {
    dailyPresenceAttempts.delete(key)
    captureLastLoginError(error, 'trackDailyPresence')
    console.error('Error tracking daily presence:', error)
  }
}

export const trackUserLogin = async (userOrUserId) => {
  let userId, userEmail, displayName

  // Handle both user object and userId string
  if (typeof userOrUserId === 'string') {
    userId = userOrUserId
    // Get user details from Firestore if only userId is provided
    try {
      const userDocRef = doc(db, 'users', userId)
      const userDoc = await getDoc(userDocRef)
      if (userDoc.exists()) {
        const userData = userDoc.data()
        userEmail = userData.email || ''
        displayName =
          userData.firstName && userData.lastName
            ? `${userData.firstName} ${userData.lastName}`
            : userData.name || ''
      }
    } catch (error) {
      captureLastLoginError(error, 'trackUserLogin.fetchDetails')
      console.error('Error fetching user details for login tracking:', error)
    }
  } else if (userOrUserId && userOrUserId.uid) {
    userId = userOrUserId.uid
    userEmail = userOrUserId.email || ''
    displayName = userOrUserId.displayName || ''
  } else {
    console.error('Invalid user data provided to trackUserLogin')
    return
  }

  if (!userId) {
    console.error('User ID is required for login tracking')
    return
  }

  try {
    const userDocRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userDocRef)

    // Use Firestore Timestamp for consistent timing
    const currentTimestamp = Timestamp.now()

    if (userDoc.exists()) {
      // User exists, increment login count
      const userData = userDoc.data()
      const currentLoginCount = userData.loginCount || 0

      await updateDoc(userDocRef, {
        lastLogin: currentTimestamp,
        loginHistory: arrayUnion(currentTimestamp),
        name: displayName || userData.name || '',
        email: userEmail || userData.email || '',
        loginCount: currentLoginCount + 1
      })
    } else {
      await setDoc(userDocRef, {
        lastLogin: currentTimestamp,
        loginHistory: [currentTimestamp],
        name: displayName || '',
        email: userEmail || '',
        loginCount: 1,
        totalTimeSpent: 0,
        createdAt: currentTimestamp
      })
    }
  } catch (error) {
    captureLastLoginError(error, 'trackUserLogin')
    console.error('Error tracking user login:', error)
  }
}

/**
 * Updates user data without affecting login tracking
 * @param {string} userId - The user's UID
 * @param {Object} data - Data to update
 */
export const updateUserData = async (userId, data) => {
  if (!userId) {
    console.error('User ID is required for updateUserData')
    return
  }

  try {
    const userDocRef = doc(db, 'users', userId)
    await updateDoc(userDocRef, data)
  } catch (error) {
    console.error('Error updating user data:', error)
  }
}
